import { CalendarIcon, ClockIcon, DurationIcon, GiftIcon } from "@xola/icons";
import { Badge } from "@xola/ui-kit";
import clsx from "clsx";
import { DateFormat, formatDate, isBaseGiftProduct, isExperienceProduct, isGiftOfferProduct } from "common";
import dayjs from "dayjs";
import { isNil, isUndefined } from "lodash";
import React, { ReactNode, useState } from "react";
import { Experience, GiftOffer, Package, PurchaseItem } from "types";
import { isScheduled } from "../../models/Product";
import { CreatePurchaseStep, useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { NoTranslate } from "../Common/NoTranslate";
import { ProductImage } from "../Common/ProductImage";
import { SecondaryButton } from "../Common/SecondaryButton";
import { GiftOfferTerm } from "../Product/GiftOfferTile";
import { PurchaseSummaryModifyOptions } from "./PurchaseSummaryModifyOptions";
import { formatDuration } from "@/helpers/durationHelper";
import { useProduct } from "@/hooks/resources/useProduct";
import { isGiftPurchaseItem } from "@/models/PurchaseItem";

interface PurchaseItemSummaryProps {
    item: PurchaseItem;
    isActive?: boolean;
    itemIndex: number;
    children: ReactNode;
    canEdit?: boolean;
    onEditClick?: () => void;
}

export const PurchaseItemSummary = ({
    item,
    itemIndex,
    isActive = false,
    canEdit = false,
    onEditClick,
    children,
}: PurchaseItemSummaryProps) => {
    const step = useCreatePurchaseStore((state) => state.step);
    const { data: product, isLoading } = useProduct<Experience | GiftOffer | Package>(item.product.id);
    const isVariableDemographicsPackage = useCreatePurchaseStore((state) => state.isVariableDemographicsPackage());
    const [disableEditButton, setDisableEditButton] = useState(false);
    const activePurchaseItemId = useCreatePurchaseStore((state) => state.getActiveItem().id);
    const isGiftItem = useCreatePurchaseStore((state) => isGiftPurchaseItem(state.getActiveItem()));
    const timezoneName = useCreatePurchaseStore((state) => state.purchase.items[itemIndex]?.arrivalTimezoneName);

    if (isLoading || !product) {
        return null;
    }

    // If the item is deleted, edit button show up until api call is returned with deleted item response

    const hasArrivalDateTime = "arrivalDateTime" in item;

    const itemArrivalDateTime = getItemArrivalDateTime(item, timezoneName);
    const formattedArrivalDateTime = itemArrivalDateTime
        ? itemArrivalDateTime.format(DateFormat.DAY_WITH_FULL_MONTH_NAME_DATE)
        : null;

    const eventDuration = isExperienceProduct(product) ? product.eventDuration ?? product.duration ?? 0 : 0;

    const startDate = itemArrivalDateTime ? itemArrivalDateTime.format(DateFormat.DAY_WITH_FULL_MONTH_NAME_DATE) : null;
    const endDate = itemArrivalDateTime
        ? itemArrivalDateTime
              .add(eventDuration, "minutes")
              .subtract(1, "seconds")
              .format(DateFormat.DAY_WITH_FULL_MONTH_NAME_DATE)
        : null;
    const formattedRangeDate = `${startDate} - ${endDate}`;

    const hasArrivalTime = hasArrivalDateTime && !isNil(item.arrivalTime);

    const isActiveItem = item.id ? activePurchaseItemId === item.id : isUndefined(activePurchaseItemId);

    const isCheckout = step === CreatePurchaseStep.CHECKOUT;
    const showModifyOptions = isActive || (isVariableDemographicsPackage && isCheckout);

    return (
        <div
            className={clsx(
                "space-y-3 rounded py-5 px-6",
                getPurchaseItemClasses(isActive, isVariableDemographicsPackage, isCheckout),
            )}
            data-testid="purchase-item-summary-product"
        >
            <div className="flex justify-between space-x-1">
                <div className="flex items-center">
                    {isBaseGiftProduct(product) ? (
                        <div className="mr-3 flex-shrink-0 rounded-lg border border-gray bg-white p-2">
                            <GiftIcon size="medium" />
                        </div>
                    ) : (
                        <ProductImage product={product} size="small" className="!mr-1" />
                    )}

                    <div className="w-full">
                        <div className="text-md font-bold">{product.name}</div>

                        {isGiftOfferProduct(product) ? (
                            product.baseGiftProduct || product.experience?.id ? null : (
                                <div className="space-x-2">
                                    <Badge color="secondary" size="medium">
                                        <GiftOfferTerm giftOffer={product} />
                                    </Badge>
                                </div>
                            )
                        ) : hasArrivalDateTime || item.expiresAt ? (
                            <div className="flex w-full space-x-2 text-gray-darker">
                                {/* TODO: This bit needs a component, we re-use it in many places */}
                                <span>
                                    <CalendarIcon />
                                    &nbsp;
                                    {hasArrivalDateTime && itemArrivalDateTime && isScheduled(product) ? (
                                        <NoTranslate data-testid="purchase-item-summary-date">
                                            {/* TODO: Responsive show DATE instead of DATE_LONG */}

                                            {eventDuration >= 1440 ? formattedRangeDate : formattedArrivalDateTime}
                                        </NoTranslate>
                                    ) : (
                                        item.expiresAt && (
                                            <span data-testid="purchase-item-summary-expire-date">
                                                Valid until&nbsp;
                                                <NoTranslate>
                                                    {formatDate(item.expiresAt, DateFormat.DATE_LONG)}
                                                </NoTranslate>
                                            </span>
                                        )
                                    )}
                                </span>
                                {hasArrivalTime ? (
                                    <NoTranslate data-testid="purchase-item-summary-time" className="whitespace-nowrap">
                                        <ClockIcon /> {itemArrivalDateTime?.format(DateFormat.TIME)}
                                    </NoTranslate>
                                ) : null}
                                {isExperienceProduct(product) && isScheduled(product) && product.eventDuration ? (
                                    <span className="flex items-center space-x-1">
                                        <DurationIcon />
                                        <NoTranslate data-testid="purchase-item-summary-duration">
                                            {formatDuration(product.eventDuration)}
                                        </NoTranslate>
                                    </span>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                </div>

                <div className="items-top flex space-x-1">
                    {(!isActiveItem || (canEdit && !isGiftItem)) && (
                        <SecondaryButton
                            size="small"
                            data-testid="purchase-item-summary-edit-button"
                            disabled={disableEditButton}
                            onClick={onEditClick}
                        >
                            Modify
                        </SecondaryButton>
                    )}
                </div>
            </div>

            {children}

            {showModifyOptions && (
                <PurchaseSummaryModifyOptions
                    item={item}
                    isActive={isActive}
                    itemIndex={itemIndex}
                    setDisableEditButton={setDisableEditButton}
                />
            )}
        </div>
    );
};

PurchaseItemSummary.displayName = "PurchaseItemSummary";

const getItemArrivalDateTime = (purchaseItem: PurchaseItem, timezoneName?: string) => {
    const hasArrivalDateTime = "arrivalDateTime" in purchaseItem;

    if (hasArrivalDateTime) {
        const { arrivalDateTime } = purchaseItem;
        return timezoneName ? dayjs(arrivalDateTime).tz(timezoneName) : arrivalDateTime;
    }
};

export const getPurchaseItemClasses = (
    isActive: boolean,
    isVariableDemographicsPackage: boolean,
    isCheckout: boolean,
) => {
    let activeItem = "border border-primary bg-primary-lighter";
    let inactiveItem = "bg-white";

    if (isVariableDemographicsPackage) {
        inactiveItem = "border border-white bg-white hover:border hover:border-gray hover:bg-gray-hover";
        activeItem = isCheckout ? inactiveItem : activeItem;
    }

    return isActive ? activeItem : inactiveItem;
};
